import { getScrollContainer, getScrollEventTarget, getScrollTop, scrollContainerMediaQuery } from '@theme/scroll-container';

const ROOT = document.documentElement;
const VIEW_KEY = 'lucid:view';
const VIEWS = ['product', 'model'];
const DEFAULT_TIMEZONE = 'Europe/Berlin';
const DAY = 86400000;

/**
 * Event helpers. Every event is a bubbling CustomEvent named lucid:<name>, dispatched on document unless a target
 * is given, so one listener on document hears all of them.
 */
const eventName = (name) => (name.startsWith('lucid:') ? name : `lucid:${name}`);

export function emit(name, detail = {}, target = document) {
  const event = new CustomEvent(eventName(name), { bubbles: true, composed: true, detail });
  target.dispatchEvent(event);
  return event;
}

export function on(name, handler, options) {
  const type = eventName(name);
  document.addEventListener(type, handler, options);
  return () => document.removeEventListener(type, handler, options);
}

export function once(name, handler) {
  return on(name, handler, { once: true });
}

/** Storage that never throws (private mode, blocked site data). */
const safeStore = (area) => ({
  get(key, fallback = null) {
    try {
      const value = window[area].getItem(key);
      return value === null ? fallback : value;
    } catch {
      return fallback;
    }
  },
  set(key, value) {
    try {
      window[area].setItem(key, String(value));
      return true;
    } catch {
      return false;
    }
  },
  remove(key) {
    try {
      window[area].removeItem(key);
    } catch {
      /* storage unavailable */
    }
  },
  getJSON(key, fallback = null) {
    const raw = this.get(key);
    if (raw === null) return fallback;
    try {
      return JSON.parse(raw);
    } catch {
      return fallback;
    }
  },
  setJSON(key, value) {
    return this.set(key, JSON.stringify(value));
  },
});

export const storage = safeStore('localStorage');
export const session = safeStore('sessionStorage');

/**
 * Page scroll listener that follows the scroll container: the document on mobile, `.page-wrapper` in the desktop
 * squeeze layout, where the window never fires scroll. Returns an unsubscribe function.
 */
export function onPageScroll(handler) {
  let target = getScrollEventTarget();
  target.addEventListener('scroll', handler, { passive: true });
  const rebind = () => {
    target.removeEventListener('scroll', handler);
    target = getScrollEventTarget();
    target.addEventListener('scroll', handler, { passive: true });
    handler();
  };
  scrollContainerMediaQuery.addEventListener('change', rebind);
  return () => {
    target.removeEventListener('scroll', handler);
    scrollContainerMediaQuery.removeEventListener('change', rebind);
  };
}

export const pageScrollTop = () => getScrollTop();

export const pageScrollBy = (top) => getScrollContainer().scrollBy({ top, behavior: 'instant' });

export const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* View mode: product shots or model shots, shared by every product card on the page. */

export function getView() {
  return ROOT.dataset.lucidView === 'model' ? 'model' : 'product';
}

export function setView(view, { persist = true } = {}) {
  if (!VIEWS.includes(view)) return;
  const changed = getView() !== view;
  ROOT.dataset.lucidView = view;
  if (persist) storage.set(VIEW_KEY, view);
  syncSegmented('view', view);
  if (changed) emit('view:change', { view });
}

window.addEventListener('storage', (event) => {
  if (event.key === VIEW_KEY && VIEWS.includes(event.newValue)) setView(event.newValue, { persist: false });
});

/* Segmented controls rendered by snippets/lucid-ui-segmented.liquid */

export function syncSegmented(name, value) {
  for (const group of document.querySelectorAll('[data-lucid-segmented]')) {
    if (group.dataset.lucidSegmented !== name) continue;
    let index = 0;
    group.querySelectorAll('.lucid-segmented__option').forEach((option, position) => {
      const pressed = option.dataset.value === value;
      option.setAttribute('aria-pressed', String(pressed));
      if (pressed) index = position;
    });
    group.classList.toggle('is-second', index === 1);
  }
}

document.addEventListener('click', (event) => {
  const option = event.target instanceof Element ? event.target.closest('.lucid-segmented__option') : null;
  const group = option?.closest('[data-lucid-segmented]');
  if (!option || !group || option.getAttribute('aria-pressed') === 'true') return;

  const name = group.dataset.lucidSegmented;
  const value = option.dataset.value;
  if (name === 'view') {
    setView(value);
  } else {
    syncSegmented(name, value);
  }
  emit('segmented:change', { name, value }, group);
});

/* Scroll reveal: one IntersectionObserver for every [data-lucid-reveal] on the page. */

let revealObserver = null;
const STAGGER_STEP = 60;
const STAGGER_MAX = 8;

function reveal(element) {
  element.classList.add('is-revealed');
  revealObserver?.unobserve(element);
}

function prepare(element) {
  if (element.dataset.lucidRevealReady) return;
  element.dataset.lucidRevealReady = '';
  if (element.dataset.lucidReveal === 'stagger') {
    Array.from(element.children).forEach((child, index) => {
      child.style.setProperty('--lc-reveal-delay', `${Math.min(index, STAGGER_MAX) * STAGGER_STEP}ms`);
    });
  }
  revealObserver.observe(element);
}

export function observeReveal(root = document) {
  if (!revealObserver) return;
  if (root instanceof Element && root.matches('[data-lucid-reveal]')) prepare(root);
  root.querySelectorAll?.('[data-lucid-reveal]').forEach(prepare);
}

function initReveal() {
  if (!ROOT.classList.contains('lucid-reveal')) return;
  if (!('IntersectionObserver' in window) || prefersReducedMotion()) {
    ROOT.classList.remove('lucid-reveal');
    return;
  }
  revealObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) reveal(entry.target);
      }
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
  );
  observeReveal(document);
}

/* Lazy image fade: the head script marks images on load; this keeps the mark when markup is morphed. */

function markComplete(root = document) {
  const images = root instanceof Element && root.matches('.lucid-media__img') ? [root] : [];
  root.querySelectorAll?.('.lucid-media__img:not(.is-loaded)').forEach((image) => images.push(image));
  for (const image of images) {
    if (image.complete) image.classList.add('is-loaded');
  }
}

const mutationObserver = new MutationObserver((records) => {
  for (const record of records) {
    if (record.type === 'attributes') {
      const target = record.target;
      if (target.classList.contains('lucid-media__img') && !target.classList.contains('is-loaded') && target.complete) {
        target.classList.add('is-loaded');
      }
      continue;
    }
    for (const node of record.addedNodes) {
      if (!(node instanceof Element)) continue;
      markComplete(node);
      observeReveal(node);
    }
  }
});

/* Time helpers for drops and delivery dates, all in a named IANA time zone. */

const partFormatters = new Map();

function formatterFor(timeZone) {
  if (!partFormatters.has(timeZone)) {
    partFormatters.set(
      timeZone,
      new Intl.DateTimeFormat('en-US', {
        timeZone,
        hourCycle: 'h23',
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
      })
    );
  }
  return partFormatters.get(timeZone);
}

/** Wall clock parts of a moment in a time zone: { year, month, day, hour, minute, second, weekday }. */
export function zonedParts(date, timeZone = DEFAULT_TIMEZONE) {
  const parts = {};
  for (const { type, value } of formatterFor(timeZone).formatToParts(date)) {
    if (type !== 'literal') parts[type] = Number(value);
  }
  parts.weekday = new Date(Date.UTC(parts.year, parts.month - 1, parts.day)).getUTCDay();
  return parts;
}

function zoneOffset(timestamp, timeZone) {
  const p = zonedParts(new Date(timestamp), timeZone);
  return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second) - Math.floor(timestamp / 1000) * 1000;
}

/** Converts a wall clock time in a time zone to a timestamp. Day overflow (day 32) rolls into the next month. */
export function zonedTimeToUtc(year, month, day, hour, minute, timeZone = DEFAULT_TIMEZONE) {
  const guess = Date.UTC(year, month - 1, day, hour, minute);
  const offset = zoneOffset(guess, timeZone);
  let timestamp = guess - offset;
  const corrected = zoneOffset(timestamp, timeZone);
  if (corrected !== offset) timestamp = guess - corrected;
  return timestamp;
}

/** Parses "HH:MM" into [hour, minute], or null. */
export function parseTime(value) {
  const match = /^(\d{1,2}):(\d{2})$/.exec(String(value || '').trim());
  if (!match) return null;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  return hour < 24 && minute < 60 ? [hour, minute] : null;
}

/** Parses an ISO date. Without an offset the wall clock is read in the given zone. Returns ms or null. */
export function parseZoned(value, timeZone = DEFAULT_TIMEZONE) {
  const text = String(value || '').trim().replace(' ', 'T');
  const local = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::\d{2})?$/.exec(text);
  if (local) {
    const [, year, month, day, hour, minute] = local.map(Number);
    return zonedTimeToUtc(year, month, day, hour, minute, timeZone);
  }
  const parsed = Date.parse(text);
  return Number.isNaN(parsed) ? null : parsed;
}

/**
 * Puts a drop scope into a state (running, live or ended). Dated parts show until the end, fallback parts after it,
 * and `[data-drop-when]` parts only in the states they list.
 */
export function syncDropScope(element, state) {
  const scope = element.closest('[data-drop-scope]');
  if (!scope) {
    if (state === 'ended') element.hidden = true;
    return;
  }
  if (scope.dataset.dropState === state) return;
  scope.dataset.dropState = state;
  const ended = state === 'ended';
  for (const node of scope.querySelectorAll('[data-drop-dated]')) node.hidden = ended;
  for (const node of scope.querySelectorAll('[data-drop-fallback]')) node.hidden = !ended;
  for (const node of scope.querySelectorAll('[data-drop-when]')) {
    node.hidden = !node.dataset.dropWhen.split(/\s+/).includes(state);
  }
}

/** Hides the dated parts of a drop scope and reveals its fallback, once the drop and its live window are over. */
export function endDrop(element) {
  syncDropScope(element, 'ended');
}

function expireDrops(root = document) {
  const now = Date.now();
  for (const scope of root.querySelectorAll('[data-drop-scope][data-drop-end]')) {
    const end = Number(scope.dataset.dropEnd);
    if (Number.isFinite(end) && end > 0 && now >= end) endDrop(scope.querySelector('[data-drop-dated]') || scope);
  }
}

/* <lucid-countdown> */

class LucidCountdown extends HTMLElement {
  static observedAttributes = ['timezone', 'datetime', 'live-minutes'];

  #timer = 0;
  #target = null;
  #ended = false;

  connectedCallback() {
    this.#start();
    document.addEventListener('visibilitychange', this.#onVisibility);
  }

  disconnectedCallback() {
    this.#stop();
    document.removeEventListener('visibilitychange', this.#onVisibility);
  }

  attributeChangedCallback() {
    if (this.isConnected) this.#start();
  }

  /** Target timestamp in ms, or null without a valid datetime. */
  get target() {
    return this.#target;
  }

  get #liveMs() {
    const minutes = Number(this.getAttribute('live-minutes'));
    return Number.isFinite(minutes) && minutes > 0 ? minutes * 60000 : 0;
  }

  #onVisibility = () => {
    if (document.visibilityState === 'visible' && this.isConnected && this.#timer) this.#tick();
  };

  #start() {
    this.#stop();
    this.#ended = false;
    this.#target = parseZoned(this.getAttribute('datetime'), this.getAttribute('timezone') || DEFAULT_TIMEZONE);
    if (this.#target === null) {
      this.dataset.state = 'invalid';
      endDrop(this);
      return;
    }
    this.#tick();
    if (!this.#ended) this.#timer = window.setInterval(() => this.#tick(), 1000);
  }

  #stop() {
    window.clearInterval(this.#timer);
    this.#timer = 0;
  }

  #tick() {
    const now = Date.now();
    const remaining = Math.max(0, this.#target - now);

    if (remaining === 0 && now >= this.#target + this.#liveMs) {
      this.dataset.state = 'ended';
      this.#ended = true;
      this.#stop();
      emit('countdown:end', { target: new Date(this.#target).toISOString() }, this);
      endDrop(this);
      return;
    }

    const state = remaining === 0 ? 'live' : 'running';
    this.dataset.state = state;
    this.setAttribute('data-target', new Date(this.#target).toISOString());
    syncDropScope(this, state);
    this.#render(remaining);
  }

  #render(remaining) {
    const units = {
      days: Math.floor(remaining / DAY),
      hours: Math.floor((remaining % DAY) / 3600000),
      minutes: Math.floor((remaining % 3600000) / 60000),
      seconds: Math.floor((remaining % 60000) / 1000),
    };
    if (!this.querySelector('[data-unit="days"]')) units.hours += units.days * 24;

    for (const element of this.querySelectorAll('[data-unit]')) {
      const unit = element.dataset.unit;
      if (!(unit in units)) continue;
      const text = unit === 'days' ? String(units[unit]) : String(units[unit]).padStart(2, '0');
      if (element.textContent !== text) element.textContent = text;
    }
  }
}

if (!customElements.get('lucid-countdown')) customElements.define('lucid-countdown', LucidCountdown);

/* Boot */

function boot() {
  syncSegmented('view', getView());
  expireDrops();
  initReveal();
  markComplete(document);
  mutationObserver.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['class'],
  });
  ROOT.classList.add('lucid-core-ready');
  emit('core:ready');
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot, { once: true });
} else {
  boot();
}

window.Lucid = Object.freeze({ emit, on, once, storage, session, getView, setView, observeReveal, parseZoned, endDrop, syncDropScope, zonedTimeToUtc, zonedParts });
